import 'regenerator-runtime/runtime';
import { SagaIterator } from 'redux-saga';
import { call, delay, put, race, select, take, takeLatest } from 'redux-saga/effects';
import { GlobalActions } from '@ps20-frontend/shop-integration-redux';
import { App, isTiergruppeActive } from '@ps20-frontend/frontend-models';
import { PS20Logger } from '@ps20-frontend/logging';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { IAppState } from '../appState';
import { childOfKey } from '../models/childKeys';
import { selectableChildKeys } from '../models/angebotOffers';
import { TgvActions } from '../tgvActions';
import { isLeavingAngebot } from './angebotLifecycle';
import { sharedDispatcher } from './sharedDispatcher';

export const ANGEBOT_HEAL_GRACE_MS = 10000;

const REGISTRATION_EVENTS = [
  GlobalActions.OWNED_STORE_INITIALIZED,
  GlobalActions.ALL_MISSING_STORES_CREATED,
];

const HEAL_TRIGGERS = [
  ...REGISTRATION_EVENTS,
  GlobalActions.PRODUKT_DELETED,
  TgvActions.TGV_ANGEBOTS_ID_ASSIGNED,
  TgvActions.TGV_CHILD_CREATING_REMOVED,
  TgvActions.TGV_PENDING_DELETION_CLEARED,
];

const isTkvKey = (key: string): boolean => childOfKey(key).appId === App.TKV_V2;

const storelessKeys = ({ tiergruppe, transientState }: IAppState): string[] =>
  selectableChildKeys(tiergruppe, transientState).filter(
    key => isTkvKey(key) && sharedDispatcher().findStoreRef(App.TKV_V2, key) === undefined,
  );

export function* watchAngebotChildHeal(): SagaIterator {
  yield takeLatest(TgvActions.TGV_ANGEBOT_OPENED, safeSaga(angebotChildHealSaga, App.TGV));
}

export function* angebotChildHealSaga(): SagaIterator {
  const { transientState }: IAppState = yield select();
  if (!isTiergruppeActive(transientState.featureToggles)) {
    return;
  }
  yield race({ healing: call(healLoop), left: take(isLeavingAngebot) });
}

function* healLoop(): SagaIterator {
  while (true) {
    const firstRead: IAppState = yield select();
    const missingKeys = storelessKeys(firstRead);
    if (missingKeys.length === 0) {
      yield take(HEAL_TRIGGERS);
      continue;
    }
    const { registered } = yield race({
      registered: take(REGISTRATION_EVENTS),
      graceElapsed: delay(ANGEBOT_HEAL_GRACE_MS),
    });
    if (registered) {
      continue;
    }
    const secondRead: IAppState = yield select();
    const healedKeys = storelessKeys(secondRead).filter(key => missingKeys.includes(key));
    if (healedKeys.length > 0) {
      PS20Logger.info(
        'TGV_ANGEBOT_CHILD_LINKS_HEALED',
        `Dropped child links without a registered store after ${ANGEBOT_HEAL_GRACE_MS} ms on the Angebot page: ${healedKeys.join(', ')}`,
        App.TGV,
      );
      yield put(new TgvActions().childLinksHealed(healedKeys));
    }
  }
}
